"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"; // Added useRef
import { useAuth } from "./auth-context";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from "@/lib/firebase/config";
import { useTheme } from "next-themes";

// Types for user preferences and navigation history
interface UserPreferences {
  theme?: "light" | "dark" | "system";
  sidebarCollapsed?: boolean;
  codeTheme?: string;
  fontSize?: "small" | "medium" | "large";
  docVersionPreferences?: Record<string, string>; // framework -> preferred version
}

interface NavigationHistoryItem {
  path: string;
  title: string;
  timestamp: Date;
}

interface UsageStats {
  lastActive: Date;
  pageViews: Record<string, number>; // path -> view count
  apiCalls: number;
  docsSearches: number;
}

interface UserContextType {
  // Consolidated user data
  preferences: UserPreferences;
  navigationHistory: NavigationHistoryItem[];
  usageStats: UsageStats;

  // User state functions
  updatePreferences: (newPrefs: Partial<UserPreferences>) => Promise<void>;
  trackPageVisit: (path: string, title: string) => void;
  trackApiCall: () => void;
  trackDocsSearch: () => void;
  clearHistory: () => Promise<void>;
}

// Get initial theme from localStorage or system preference
const getInitialTheme = (): "light" | "dark" | "system" => {
  try {
    if (typeof window !== 'undefined') {
      // First check localStorage
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme === "light" || savedTheme === "dark" || savedTheme === "system") {
        return savedTheme;
      }

      // If no saved theme, check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return "dark";
      }
    }
  } catch (error) {
    console.error("Error getting initial theme:", error);
  }
  return "system";
};

const initialPreferences: UserPreferences = {
  theme: getInitialTheme(),
  sidebarCollapsed: false,
  codeTheme: "github",
  fontSize: "medium",
  docVersionPreferences: {}
};

const initialUsageStats: UsageStats = {
  lastActive: new Date(),
  pageViews: {},
  apiCalls: 0,
  docsSearches: 0
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isInitialLoad = useRef(true); // Ref to track initial load

  const [preferences, setPreferences] = useState<UserPreferences>(initialPreferences);
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats>(initialUsageStats);

  // Load user data from Firestore when user changes
  useEffect(() => {
    const loadUserData = async () => {
      isInitialLoad.current = true; // Mark start of load for this user session
      if (!user) {
        // User logged out: Reset history/stats, keep preferences in memory (theme etc.)
        setNavigationHistory([]);
        setUsageStats(initialUsageStats);
        // Don't reset preferences on logout
        return;
      }

      try {
        const userDataDoc = doc(db, "userWebdata", user.uid);
        const docSnapshot = await getDoc(userDataDoc);

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();

          // Load or initialize preferences
          if (data.preferences) {
            // Load from Firestore, merging with defaults for any missing keys
            setPreferences({
              ...initialPreferences, // Start with defaults
              ...data.preferences    // Override with saved data
            });
          } else {
            // New user or no preferences saved yet.
            // Save the initialPreferences to Firestore.
            // No need to save here explicitly, the save effect will handle it if needed after load.
            setPreferences(initialPreferences); // Set state to defaults
          }

          // Load navigation history
          if (data.navigationHistory) {
            // Convert Firestore timestamps to Date objects
            const history = data.navigationHistory.map((item: any) => ({
              ...item,
              timestamp: item.timestamp?.toDate() || new Date()
            }));
            setNavigationHistory(history);
          }

          // Load usage stats
          if (data.usageStats) {
            setUsageStats({
              ...data.usageStats,
              lastActive: data.usageStats.lastActive?.toDate() || new Date()
            });
          }
        } else {
          // New user - create initial document with preferences
          await setDoc(userDataDoc, {
            userId: user.uid,
            preferences: initialPreferences,
            navigationHistory: [],
            usageStats: initialUsageStats,
            lastUpdated: new Date()
          });
          setPreferences(initialPreferences);
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        isInitialLoad.current = false; // Mark end of load attempt
      }
    };

    loadUserData();
  }, [user]);

  // Save specific parts of user data to Firestore when they change
  const saveDataToFirestore = useCallback(async (dataToSave: Record<string, any>) => {
    if (!user) return;
    try {
      const userDataDoc = doc(db, "userWebdata", user.uid);
      await setDoc(userDataDoc, {
        userId: user.uid,
        ...dataToSave,
        lastUpdated: new Date()
      }, { merge: true });
    } catch (error) {
      console.error("Error saving data to Firestore:", error);
    }
  }, [user]);

  // Save preferences when they change, *after* initial load
  useEffect(() => {
    if (user && !isInitialLoad.current) {
       saveDataToFirestore({ preferences });
    }
  }, [user, preferences, saveDataToFirestore]); // Keep dependencies

  // Save navigation history when it changes, *after* initial load
  useEffect(() => {
    // Avoid saving empty initial history & during initial load
    if (user && !isInitialLoad.current && navigationHistory.length > 0) {
      saveDataToFirestore({ navigationHistory });
    }
  }, [user, navigationHistory, saveDataToFirestore]); // Keep dependencies

  // Save usage stats when they change, *after* initial load
  useEffect(() => {
     // Avoid saving initial stats & during initial load
    if (user && !isInitialLoad.current) {
       saveDataToFirestore({ usageStats });
    }
  }, [user, usageStats, saveDataToFirestore]); // Keep dependencies


  const { setTheme } = useTheme();

  // Effect to sync theme between next-themes and preferences
  useEffect(() => {
    if (preferences.theme) {
      setTheme(preferences.theme);
      // Store in localStorage for persistence
      localStorage.setItem("theme", preferences.theme);
    }
  }, [preferences.theme, setTheme]);

  // Effect to handle system theme changes
  useEffect(() => {
    if (preferences.theme === "system") {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setTheme(e.matches ? "dark" : "light");
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [preferences.theme, setTheme]);

  // Update user preferences
  const updatePreferences = async (newPrefs: Partial<UserPreferences>) => {
    const updatedPreferences = { ...preferences, ...newPrefs };
    setPreferences(updatedPreferences);

    // Update theme if it's changed
    if (newPrefs.theme) {
      setTheme(newPrefs.theme);
      localStorage.setItem("theme", newPrefs.theme);
    }

    // Preferences state is updated, the dedicated useEffect will handle saving.
  };

  // Track page visits
  const trackPageVisit = (path: string, title: string) => {
    // Update navigation history
    const newHistoryItem = {
      path,
      title,
      timestamp: new Date()
    };

    // Add to front, limit to 20 items
    const updatedHistory = [
      newHistoryItem,
      ...navigationHistory.filter(item => item.path !== path)
    ].slice(0, 20);

    setNavigationHistory(updatedHistory);

    // Update page view counts
    setUsageStats(prev => {
      const pageViews = { ...prev.pageViews };
      pageViews[path] = (pageViews[path] || 0) + 1;

      return {
        ...prev,
        lastActive: new Date(),
        pageViews
      };
    });
  };

  // Track API calls
  const trackApiCall = () => {
    setUsageStats(prev => ({
      ...prev,
      lastActive: new Date(),
      apiCalls: prev.apiCalls + 1
    }));
  };

  // Track docs searches
  const trackDocsSearch = () => {
    setUsageStats(prev => ({
      ...prev,
      lastActive: new Date(),
      docsSearches: prev.docsSearches + 1
    }));
  };

  // Clear history
  const clearHistory = async () => {
    setNavigationHistory([]);

    // Use the dedicated save function
    if (user) {
       saveDataToFirestore({ navigationHistory: [] });
    }
  };

  return (
    <UserContext.Provider
      value={{
        preferences,
        navigationHistory,
        usageStats,
        updatePreferences,
        trackPageVisit,
        trackApiCall,
        trackDocsSearch,
        clearHistory
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
